import type { FileVersion } from "schemas/generated/fileVersion";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import { useViewingVersionId } from "@v2/store/editorStore";
import { fileVersionRouter } from "@v2/trpc/routes/fileVersion";
import { useSearchParams } from "react-router-dom";

interface UseViewingVersionReturn {
  fileVersion: FileVersion | undefined;
  isFileVersionLoading: boolean;
  isCurrentVersion: boolean;
  isViewingVersion: boolean;
}

/**
 * Consolidated hook for managing file version viewing state.
 * Replaces the previous useViewingVersionId hook and encapsulates all file version logic.
 *
 * @param fileVersionId - Optional specific file version ID to get data for
 * @param options - Additional options
 * @param options.shouldIncludeDeleted - Whether to include deleted file versions in queries
 */
export function useViewingVersion(
  fileVersionId?: string,
  options: {
    shouldIncludeDeleted?: boolean;
  } = {},
): UseViewingVersionReturn {
  const { shouldIncludeDeleted = true } = options;

  const [searchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");
  const viewingVersionId = useViewingVersionId();

  const shouldSkipFileVersionQuery = !fileVersionId;
  const { data: fileVersion, isLoading: isFileVersionLoading } =
    fileVersionRouter.get.useQuery(
      shouldSkipFileVersionQuery
        ? skipToken
        : {
            id: fileVersionId,
            shouldIncludeDeleted,
          },
    );

  const { data: fileVersionsData } =
    fileVersionRouter.findByFileId.useInfiniteQuery(
      pageId
        ? {
            fileId: pageId,
            limit: 1,
          }
        : skipToken,
      {
        getNextPageParam: ({ nextCursor }) => nextCursor,
      },
    );

  const fileVersions = React.useMemo(
    () => fileVersionsData?.pages?.flatMap((page) => page.fileVersions) ?? [],
    [fileVersionsData],
  );

  const currentVersion = fileVersions?.[0];
  const isCurrentVersion = Boolean(
    fileVersionId && currentVersion?.id === fileVersionId,
  );
  const isViewingVersion = Boolean(
    fileVersionId && viewingVersionId === fileVersionId,
  );

  return {
    fileVersion,
    isFileVersionLoading,
    isCurrentVersion,
    isViewingVersion,
  };
}
